import compact from 'lodash-es/compact'
import { useMemo } from 'react'
import { useObservable } from 'react-use'

import { QueryStatus } from '../../lib/hooks/types'
import { NostoPlacementId, useNostoClient } from '../_components/chrome/scripts/NostoScript'

export const useFetchNostoPlacementData = (
  nostoPlacementId: NostoPlacementId,
  options?: {
    skip?: boolean
    ignoreFallback?: boolean
  }
) => {
  const skip = options?.skip ?? false
  const nostoClient = useNostoClient()
  const nostoRecommendationsByPlacementId = useObservable(nostoClient.recommendations$, {})
  const selectedNostoRecommendationCollection = nostoRecommendationsByPlacementId[nostoPlacementId]

  const [nostoRecommendedSkuCodes, nostoRecommendedDeprecatedProductHandles] = useMemo<
    [string[], string[]]
  >(() => {
    if (
      !selectedNostoRecommendationCollection ||
      (options?.ignoreFallback === true &&
        selectedNostoRecommendationCollection.result_id !== nostoPlacementId)
    ) {
      return [[], []] as const
    }

    const products =
      selectedNostoRecommendationCollection?.products.map(p => {
        const sku = (p.skus ?? []).concat().sort((thisSku, thatSku) => {
          return thisSku.available && thatSku.available ? 0 : thisSku.available ? -1 : 1
        })[0]?.custom_fields.skuCode
        const handle = p.handle
        return {
          sku,
          handle,
        }
      }) ?? []

    return [compact(products.map(p => p.sku)), compact(products.map(p => p.handle))] as const
  }, [nostoPlacementId, options?.ignoreFallback, selectedNostoRecommendationCollection])

  const status = useMemo<Exclude<QueryStatus, 'idle'>>(() => {
    if (skip) return 'resolved'

    // We are waiting on Nosto JS
    if (!selectedNostoRecommendationCollection) return 'pending'

    return 'resolved'
  }, [selectedNostoRecommendationCollection, skip])

  return {
    data: {
      title: selectedNostoRecommendationCollection?.title,
      skusForVariantGroups: nostoRecommendedSkuCodes,
      handlesForDeprecatedProducts: nostoRecommendedDeprecatedProductHandles,
      resultId: selectedNostoRecommendationCollection?.result_id,
    },
    status,
  }
}
